/**
 * AR(1) Inflation Model with Mean Reversion
 * 
 * Generates realistic inflation time series that:
 * 1. Shows year-to-year persistence (like historical data)
 * 2. Preserves user's target mean and standard deviation
 * 3. Includes mean reversion to prevent drift
 */
export class InflationModel {
  private targetMean: number;
  private targetStdDev: number;
  private phi: number; // Autoregressive coefficient
  private innovationStdDev: number; // Standard deviation of random shocks
  private lastInflationRate: number | null = null;

  constructor(targetMean: number, targetStdDev: number) {
    this.targetMean = targetMean;
    this.targetStdDev = targetStdDev;
    
    // Use historical autocorrelation as our phi
    this.phi = 0.609;
    
    // Calculate innovation standard deviation to achieve target overall variance
    // For AR(1): overall_variance = innovation_variance / (1 - phi^2)
    // So: innovation_variance = overall_variance * (1 - phi^2)
    const targetVariance = targetStdDev * targetStdDev;
    const innovationVariance = targetVariance * (1 - this.phi * this.phi);
    this.innovationStdDev = Math.sqrt(innovationVariance);
  }

  /**
   * Generate the next inflation rate in the time series
   * Uses AR(1) formula: inflation[t] = μ + φ * (inflation[t-1] - μ) + ε[t]
   */
  getNextInflationRate(): number {
    // If target standard deviation is 0, always return the mean
    if (this.targetStdDev === 0) {
      this.lastInflationRate = this.targetMean;
      return this.targetMean;
    }

    // For the first year, start at the target mean plus a small random deviation
    if (this.lastInflationRate === null) {
      this.lastInflationRate = this.targetMean + this.generateNormalRandom() * this.innovationStdDev;
      return this.lastInflationRate;
    }

    // AR(1) with mean reversion formula
    const innovation = this.generateNormalRandom() * this.innovationStdDev;
    const nextRate = this.targetMean + this.phi * (this.lastInflationRate - this.targetMean) + innovation;
    
    this.lastInflationRate = nextRate;
    return nextRate;
  }

  /**
   * Reset the model for a new trial
   */
  reset(): void {
    this.lastInflationRate = null;
  }

  /**
   * Update target parameters (preserves current state)
   */
  updateTargets(newMean: number, newStdDev: number): void {
    this.targetMean = newMean;
    this.targetStdDev = newStdDev;
    
    // Recalculate innovation standard deviation
    const targetVariance = newStdDev * newStdDev;
    const innovationVariance = targetVariance * (1 - this.phi * this.phi);
    this.innovationStdDev = Math.sqrt(innovationVariance);
  }

  /**
   * Generate a standard normal random number using Box-Muller transform
   * (Similar to Parameter.ts but simplified for single values)
   */
  private generateNormalRandom(): number {
    let u = 0, v = 0;
    while (u === 0) u = Math.random(); // Converting [0,1) to (0,1)
    while (v === 0) v = Math.random();
    
    const R = Math.sqrt(-2.0 * Math.log(u));
    const theta = 2.0 * Math.PI * v;
    return R * Math.cos(theta); // Standard normal (mean=0, stddev=1)
  }

  /**
   * Get model parameters for debugging/testing
   */
  getModelParams() {
    return {
      targetMean: this.targetMean,
      targetStdDev: this.targetStdDev,
      phi: this.phi,
      innovationStdDev: this.innovationStdDev,
      currentRate: this.lastInflationRate
    };
  }
}
